// El logo se guarda en base64 dentro de gimnasios/{gymId}, que cada sesión del
// gym descarga: debe quedar liviano. Se acepta cualquier imagen que el navegador
// pueda leer (PNG, JPG, WebP, GIF, SVG, HEIC en iPhone…): siempre se redibuja en
// un lienzo cuadrado, así que lo guardado es solo una imagen rasterizada (un SVG
// nunca se guarda tal cual). Escalera de formatos y tamaños hasta que quepa:
// PNG (nítido) → WebP (conserva transparencia) → lados menores → JPG sobre
// blanco. El último peldaño (JPG chico) siempre cabe: ningún logo se rechaza por
// tener mucho detalle.
export const LIMITE_LOGO_CARACTERES = 240000;
const MAX_BYTES_ARCHIVO = 25 * 1024 * 1024;

const LADOS = [512, 384, 256];
const CALIDADES_WEBP = [0.92, 0.82, 0.7];
// JPG sobre blanco: de mejor a peor; 256 px a 0.5 pesa unos pocos KB.
const INTENTOS_JPEG = [[512, 0.88], [512, 0.75], [384, 0.7], [256, 0.6], [256, 0.5]];

const dibujar = (imagen, lado, fondo) => {
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const contexto = canvas.getContext('2d');
  if (!contexto) throw new Error('No se pudo procesar el logo.');
  if (fondo) {
    contexto.fillStyle = fondo;
    contexto.fillRect(0, 0, lado, lado);
  }
  // Margen del 12,5 % para que el logo respire en avatares y tickets.
  const escala = (lado * 0.875) / Math.max(imagen.naturalWidth, imagen.naturalHeight);
  const ancho = Math.round(imagen.naturalWidth * escala);
  const alto = Math.round(imagen.naturalHeight * escala);
  contexto.drawImage(imagen, Math.round((lado - ancho) / 2), Math.round((lado - alto) / 2), ancho, alto);
  return canvas;
};

// Un navegador sin codificador WebP devuelve PNG: solo cuenta si salió WebP.
const comoFormato = (canvas, tipo, calidad) => {
  const resultado = canvas.toDataURL(tipo, calidad);
  return resultado.startsWith(`data:${tipo};`) ? resultado : null;
};

/**
 * Recorta el margen del logo (transparente, o del color de la esquina como el
 * blanco de un JPG) y lo devuelve como PNG (o JPG si el PNG pesa demasiado),
 * máximo 512 px. Para mostrarlo sin marco y para la vista previa del enlace.
 * Sin canvas (o con un logo ilegible) devuelve null.
 */
export const recortarMargenLogo = async (dataUrl, { maxCaracteres = 800000 } = {}) => {
  if (!/^data:image\/(png|jpeg|webp);base64,/.test(dataUrl || '')) return null;
  try {
    const imagen = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
    const ancho = imagen.naturalWidth, alto = imagen.naturalHeight;
    if (!ancho || !alto) return null;
    const lienzo = document.createElement('canvas');
    lienzo.width = ancho; lienzo.height = alto;
    const ctx = lienzo.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(imagen, 0, 0);
    const { data } = ctx.getImageData(0, 0, ancho, alto);
    const fondo = data.slice(0, 4);
    const esFondo = (i) => data[i + 3] < 16
      || (fondo[3] > 240 && Math.abs(data[i] - fondo[0]) < 12 && Math.abs(data[i + 1] - fondo[1]) < 12 && Math.abs(data[i + 2] - fondo[2]) < 12);
    let x0 = ancho, y0 = alto, x1 = -1, y1 = -1;
    for (let y = 0; y < alto; y++) {
      for (let x = 0; x < ancho; x++) {
        if (!esFondo((y * ancho + x) * 4)) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
    }
    if (x1 < 0) return null;
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    const escala = Math.min(1, 512 / Math.max(w, h));
    const salida = document.createElement('canvas');
    salida.width = Math.max(1, Math.round(w * escala)); salida.height = Math.max(1, Math.round(h * escala));
    const ctxSalida = salida.getContext('2d');
    if (!ctxSalida) return null;
    ctxSalida.drawImage(lienzo, x0, y0, w, h, 0, 0, salida.width, salida.height);
    const png = salida.toDataURL('image/png');
    if (png.startsWith('data:image/png;') && png.length <= maxCaracteres) return png;
    const jpg = salida.toDataURL('image/jpeg', 0.9);
    return jpg.startsWith('data:image/jpeg;') && jpg.length <= maxCaracteres ? jpg : null;
  } catch {
    return null;
  }
};

export const comprimirLogoGym = async (archivo) => {
  if (!archivo || !String(archivo.type || '').startsWith('image/')) throw new Error('Selecciona una imagen.');
  if (archivo.size > MAX_BYTES_ARCHIVO) throw new Error('La imagen no puede superar 25 MB.');
  const url = URL.createObjectURL(archivo);
  try {
    const imagen = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Este navegador no puede leer esa imagen. Prueba con una foto o captura en PNG o JPG.'));
      img.src = url;
    });
    if (!imagen.naturalWidth || !imagen.naturalHeight) throw new Error('No se pudo leer la imagen. Prueba con una foto o captura en PNG o JPG.');
    const cabe = valor => Boolean(valor) && valor.length <= LIMITE_LOGO_CARACTERES;
    try {
      for (const lado of LADOS) {
        const canvas = dibujar(imagen, lado);
        const png = canvas.toDataURL('image/png');
        if (cabe(png)) return png;
        for (const calidad of CALIDADES_WEBP) {
          const webp = comoFormato(canvas, 'image/webp', calidad);
          if (cabe(webp)) return webp;
        }
      }
      // JPG no admite transparencia: se aplana sobre blanco, como en los tickets.
      let ultimo = null;
      for (const [lado, calidad] of INTENTOS_JPEG) {
        ultimo = comoFormato(dibujar(imagen, lado, '#FFFFFF'), 'image/jpeg', calidad);
        if (cabe(ultimo)) return ultimo;
      }
    } catch (e) {
      // Un SVG con recursos externos «ensucia» el lienzo y no se puede exportar.
      if (e?.name === 'SecurityError') throw new Error('Esa imagen no se puede procesar. Expórtala como PNG o JPG y vuelve a subirla.');
      throw e;
    }
    throw new Error('No se pudo procesar el logo. Prueba con una foto o captura en PNG o JPG.');
  } finally {
    URL.revokeObjectURL(url);
  }
};
